import React from 'react';
import { Observable } from 'rxjs';

import {
    getData,
    getErrors,
    useDataModule,
} from '@perpay-web/data-module/useDataModule';
import { ANNOUNCEMENTS_ENDPOINT } from '@perpay-web/fintech/constants/urls';
import { authenticatedJsonFetch } from '@perpay-web/fintech/utils/requestHandlers';
import { createContextProvider } from '@perpay-web/utils/componentUtils';
import { hasErrors } from '@perpay-web/utils/errorHandlers';

type AnnouncementsBannerData = {
    message: string;
    link: string;
    linkText: string;
};

type AnnouncementsBannerState = {
    requestState: string;
    errors: { [key: string]: unknown };
    value: AnnouncementsBannerData | null;
};

const initialValue: AnnouncementsBannerData = {
    message: '',
    link: '',
    linkText: '',
};

type AnnouncementsBannerDataModule = {
    state: AnnouncementsBannerState;
    dataRequest: VoidFunction;
    dataReset: VoidFunction;
    sideEffect$: Observable<unknown>;
    state$: Observable<AnnouncementsBannerState>;
    isLoadingOrUnrequested: boolean;
    isComplete: boolean;
    errors: { [key: string]: string[] };
    data: AnnouncementsBannerData;
    isError: boolean;
};

const useAnnouncementsBannerContext = () => {
    const dataModule = useDataModule(() =>
        authenticatedJsonFetch(ANNOUNCEMENTS_ENDPOINT),
    );

    return dataModule;
};

const providerFactory = (
    Context: React.Context<AnnouncementsBannerDataModule>,
) => {
    const Provider = ({
        children,
        storyOverrideValue = undefined,
    }: {
        children: React.ReactNode;
        storyOverrideValue?: AnnouncementsBannerDataModule;
    }) => {
        const dataModule = useAnnouncementsBannerContext();

        return (
            <Context.Provider value={storyOverrideValue || dataModule}>
                {children}
            </Context.Provider>
        );
    };

    return Provider;
};

export const [
    UseAnnouncementsBannerDataModuleProvider,
    useAnnouncementsBannerDataModuleContext,
] = createContextProvider(providerFactory);

export const getAnnouncementsBannerMessage = (
    state: AnnouncementsBannerState,
) => {
    if (getData(state)) {
        return getData(state).message;
    }

    if (hasErrors(getErrors(state))) {
        return 'We are currently experiencing technical difficulties. Please try again later.';
    }

    return initialValue.message;
};
